import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";

const MixcloudPlayer = ({ sourceUrl }) => {
  const iframeRef = useRef(null);
  const widgetRef = useRef(null);
  const [loading, setLoading] = useState(true); // Estado de carga

  useEffect(() => {
    // 1. Reinicia el estado de carga cada vez que el componente se monta
    // o cuando sourceUrl cambia, para mostrar el mensaje de carga de nuevo.
    setLoading(true);

    const initializeMixcloudPlayer = async () => {
      // 2. Espera a que el iframe esté disponible en el DOM
      if (!iframeRef.current) {
        console.warn("MixcloudPlayer: iframeRef no está disponible.");
        // Si el iframe no está, no podemos inicializar, pero podemos ocultar el spinner eventualmente.
        // Para este caso, el iframe siempre debería estar.
        setLoading(false);
        return;
      }

      // 3. Verifica si el SDK de Mixcloud está cargado (generalmente se carga con una etiqueta <script> globalmente).
      if (!window.Mixcloud) {
        console.warn("MixcloudPlayer: SDK de Mixcloud (window.Mixcloud) no encontrado. Asegúrate de que widget-v1.js esté cargado.");
        // Si el SDK no está, no podemos usar la API, pero el iframe básico aún puede funcionar.
        // En este caso, simplemente ocultamos el estado de carga para que el iframe se muestre.
        setLoading(false);
        return;
      }

      try {
        // 4. Inicializa el Mixcloud Player Widget, vinculándolo al iframe.
        // Si ya hay un widget, esta llamada lo reemplazará o reconfigurará para el nuevo iframe/URL.
        widgetRef.current = window.Mixcloud.PlayerWidget(iframeRef.current);

        // 5. Espera a que el widget de Mixcloud reporte que está completamente listo.
        // Esta es la parte clave para evitar la "doble carga".
        await widgetRef.current.ready;
        console.log("Mixcloud widget listo y API inicializado.");
        // 6. Solo oculta el estado de carga cuando el widget de Mixcloud está realmente listo.
        setLoading(false);
      } catch (error) {
        console.error("Error al inicializar el widget de Mixcloud:", error);
        // Si hay un error al inicializar el widget, también ocultamos el estado de carga.
        setLoading(false);
      }
    };

    initializeMixcloudPlayer();

    // Función de limpieza:
    // Si el componente se desmonta o sourceUrl cambia,
    // puedes pausar la reproducción o limpiar la referencia al widget.
    return () => {
      if (widgetRef.current && typeof widgetRef.current.pause === 'function') {
        // Opcional: Pausar el reproductor cuando el componente se desmonte o cambie la URL.
        // widgetRef.current.pause();
      }
      widgetRef.current = null; // Limpiar la referencia para evitar fugas de memoria
    };
    // IMPORTANTE: Incluye sourceUrl como dependencia.
    // Esto asegura que el efecto se vuelva a ejecutar cada vez que sourceUrl cambie,
    // re-inicializando el reproductor para el nuevo show.
  }, [sourceUrl]);

  // Codifica la URL de origen para el atributo src del iframe
  const encodedSourceUrl = encodeURIComponent(sourceUrl);

  return (
    <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", textAlign: "center" }}>
      {loading && (
        <Typography variant="body1" sx={{ mb: 1 }}>
          Cargando Player...
        </Typography>
      )}
      <iframe
        ref={iframeRef}
        title="Mixcloud Player"
        width="100%"
        height="120"
        // El iframe cargará su contenido con la URL del widget
        src={`https://www.mixcloud.com/widget/iframe/?hide_cover=1&feed=${encodedSourceUrl}`}
        frameBorder="0"
        allow="autoplay"
        // Elimina el manejador `onLoad` del iframe.
        // Ahora dependemos del evento 'ready' de la API de Mixcloud para el estado de carga.
        // onLoad={() => setLoading(false)} // ¡REMOVIDO!
        style={{ display: loading ? "none" : "block" }} // Oculta el iframe mientras está cargando
      />
    </Box>
  );
};

export default MixcloudPlayer;